import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface IncludedServicesDisplayProps {
  category?: {
    id: string;
    title: string;
    services_included?: string[];
  };
  compact?: boolean;
}

export const IncludedServicesDisplay: React.FC<IncludedServicesDisplayProps> = ({ 
  category, 
  compact = false 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!category || !category.services_included || category.services_included.length === 0) {
    return null;
  }

  if (compact) {
    const shouldShowExpandButton = category.services_included.length > 3;
    const servicesToShow = isExpanded ? category.services_included : category.services_included.slice(0, 3);
    
    return (
      <div className="bg-green-50/50 border border-green-200 rounded-lg p-3 mt-3">
        <p className="text-xs font-medium text-green-800 mb-2">
          Prestations incluses dans "{category.title}"
        </p>
        <div className="space-y-1">
          {servicesToShow.map((service, index) => (
            <div key={index} className="flex items-center gap-1">
              <Check className="h-3 w-3 text-green-600 flex-shrink-0" />
              <span className="text-xs text-green-700">{service}</span>
            </div>
          ))}
          {shouldShowExpandButton && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs text-green-600 italic hover:text-green-800 transition-colors cursor-pointer underline"
            >
              {isExpanded 
                ? "Voir moins" 
                : `+${category.services_included.length - 3} autres prestations`
              }
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Card className="border-green-200 bg-green-50/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">
          Prestations incluses dans "{category.title}"
          <Badge variant="secondary" className="bg-green-100 text-green-800 ml-2">
            {category.services_included.length} prestations
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2">
          {category.services_included.map((service, index) => (
            <div key={index} className="flex items-center gap-2">
              <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
              <span className="text-sm text-green-800">{service}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 p-3 bg-green-100/50 rounded-md">
          <p className="text-xs text-green-700">
            ✅ Ces prestations sont automatiquement incluses dans votre évènement sélectionné.
            Elles ne génèrent pas de coût supplémentaire.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};